import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShoppingBag, Minus, Plus, Truck, ShieldCheck, RefreshCw, CreditCard, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, money, categoryName } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Stars } from "@/components/Stars";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ProductCard } from "@/components/ProductCard";

const PAYMENTS = ["Visa", "Mastercard", "Amex", "Apple Pay", "Google Pay"];

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, comment: "" });

  useEffect(() => {
    window.scrollTo(0, 0);
    setActive(0);
    setQty(1);
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      api.get("/products", { params: { category: r.data.category } }).then((rr) =>
        setRelated(rr.data.filter((p) => p.id !== id).slice(0, 4))
      );
    });
    api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data));
  }, [id]);

  const avg = useMemo(
    () => (reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0),
    [reviews]
  );

  if (!product)
    return <div className="py-32 text-center text-sm text-zinc-400">Loading…</div>;

  const onSale = !!product.sale_price;
  const out = product.stock_status === "out_of_stock";

  const handleAdd = () => {
    addItem(product, qty);
    toast.success("Added to cart");
  };
  const handleBuyNow = () => {
    addItem(product, qty);
    navigate("/cart");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name");
    try {
      await api.post(`/products/${id}/reviews`, { product_id: id, ...form });
      const r = await api.get(`/products/${id}/reviews`);
      setReviews(r.data);
      setForm({ name: "", rating: 5, comment: "" });
      toast.success("Thank you for your review!");
    } catch {
      toast.error("Could not submit review");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
      <nav className="mb-8 flex items-center gap-1.5 text-xs text-zinc-400">
        <Link to="/" className="hover:text-zinc-900">Home</Link>
        <ChevronRight size={12} />
        <Link to={`/category/${product.category}`} className="hover:text-zinc-900">{categoryName(product.category)}</Link>
        <ChevronRight size={12} />
        <span className="text-zinc-600">{product.title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:gap-16">
        {/* Gallery */}
        <div className="flex flex-col gap-4">
          <div className="zoom-wrap aspect-square bg-zinc-100">
            <img
              src={imgUrl(product.images?.[active] || product.images?.[0])}
              alt={product.title}
              className="zoom-img h-full w-full object-cover"
              data-testid="pdp-main-image"
            />
          </div>
          {product.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`aspect-square overflow-hidden bg-zinc-100 ${
                    i === active ? "ring-1 ring-zinc-900" : "opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`pdp-thumb-${i}`}
                >
                  <img src={imgUrl(img)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <p className="label-eyebrow">{categoryName(product.category)}</p>
          <h1 className="font-display mt-2 text-3xl font-light leading-tight tracking-tight md:text-4xl" data-testid="pdp-title">
            {product.title}
          </h1>

          <div className="mt-3 flex items-center gap-3">
            <Stars value={avg} />
            <span className="text-xs text-zinc-500">
              {reviews.length ? `${avg.toFixed(1)} (${reviews.length} reviews)` : "No reviews yet"}
            </span>
          </div>

          <div className="mt-5 flex items-baseline gap-3" data-testid="pdp-price">
            {onSale ? (
              <>
                <span className="font-display text-3xl text-zinc-900">{money(product.sale_price)}</span>
                <span className="text-lg text-zinc-400 line-through">{money(product.regular_price)}</span>
                <span className="bg-kksale px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white">
                  Save {money(product.regular_price - product.sale_price)}
                </span>
              </>
            ) : (
              <span className="font-display text-3xl text-zinc-900">{money(product.regular_price)}</span>
            )}
          </div>

          <div
            className="rte-content mt-6 text-sm leading-relaxed text-zinc-600"
            dangerouslySetInnerHTML={{ __html: product.description }}
          />

          <div className="mt-6">
            <span
              className={`inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider ${
                out ? "text-kksale" : "text-emerald-700"
              }`}
              data-testid="pdp-stock"
            >
              <span className={`h-1.5 w-1.5 rounded-full ${out ? "bg-kksale" : "bg-emerald-600"}`} />
              {out ? "Out of Stock" : "In Stock"}
            </span>
          </div>

          {/* Conversion block */}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <div className="flex items-center border border-zinc-300">
              <button className="px-4 py-3.5" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-minus">
                <Minus size={15} />
              </button>
              <span className="min-w-10 text-center text-sm" data-testid="qty-value">{qty}</span>
              <button className="px-4 py-3.5" onClick={() => setQty((q) => q + 1)} data-testid="qty-plus">
                <Plus size={15} />
              </button>
            </div>
            <Button
              onClick={handleAdd}
              disabled={out}
              className="h-[52px] flex-1 rounded-none border border-zinc-900 bg-white text-xs uppercase tracking-widest text-zinc-900 hover:bg-zinc-50"
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag size={15} className="mr-2" /> Add to Cart
            </Button>
          </div>
          <Button
            onClick={handleBuyNow}
            disabled={out}
            className="mt-3 h-[52px] w-full rounded-none bg-zinc-900 text-xs uppercase tracking-widest hover:bg-zinc-800"
            data-testid="buy-now-btn"
          >
            Buy Now
          </Button>

          {/* Trust badges */}
          <div className="mt-6 grid grid-cols-3 gap-3 border-y border-zinc-200 py-5">
            {[
              { icon: Truck, t: "USA Shipping Only" },
              { icon: ShieldCheck, t: "Secure Checkout" },
              { icon: RefreshCw, t: "7-Day Returns" },
            ].map((b) => (
              <div key={b.t} className="flex flex-col items-center gap-2 text-center">
                <b.icon size={18} className="text-kkaccent" />
                <span className="text-xs text-zinc-500">{b.t}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <CreditCard size={16} className="text-zinc-400" />
            {PAYMENTS.map((p) => (
              <span key={p} className="border border-zinc-200 px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-500">
                {p}
              </span>
            ))}
          </div>

          {/* Accordion */}
          <Accordion type="single" collapsible defaultValue="desc" className="mt-8 border-t border-zinc-200">
            <AccordionItem value="desc" className="border-b border-zinc-200">
              <AccordionTrigger className="text-sm uppercase tracking-wider" data-testid="acc-description">Description</AccordionTrigger>
              <AccordionContent>
                <div className="rte-content text-sm leading-relaxed text-zinc-600" dangerouslySetInnerHTML={{ __html: product.description }} />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="use" className="border-b border-zinc-200">
              <AccordionTrigger className="text-sm uppercase tracking-wider" data-testid="acc-usage">How to Use / Specifications</AccordionTrigger>
              <AccordionContent>
                <ul className="list-disc space-y-2 pl-5 text-sm text-zinc-600">
                  <li>Apply as directed for best results.</li>
                  <li>Store in a cool, dry place away from direct sunlight.</li>
                  <li>For external use only. Discontinue if irritation occurs.</li>
                  <li>Cruelty-free &amp; laboratory tested.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="reviews" className="border-b border-zinc-200">
              <AccordionTrigger className="text-sm uppercase tracking-wider" data-testid="acc-reviews">
                Customer Reviews ({reviews.length})
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-5">
                  {reviews.map((r) => (
                    <div key={r.id} className="border-b border-zinc-100 pb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{r.name}</span>
                        <Stars value={r.rating} />
                      </div>
                      {r.comment && <p className="mt-1.5 text-sm text-zinc-600">{r.comment}</p>}
                    </div>
                  ))}

                  <form onSubmit={submitReview} className="space-y-3 pt-2" data-testid="review-form">
                    <p className="text-sm font-medium">Write a review</p>
                    <Input
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="rounded-none border-zinc-300"
                      data-testid="review-name"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500">Rating:</span>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          type="button"
                          key={n}
                          onClick={() => setForm({ ...form, rating: n })}
                          className={`text-lg ${n <= form.rating ? "text-kkaccent" : "text-zinc-300"}`}
                          data-testid={`review-star-${n}`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                    <Textarea
                      placeholder="Share your thoughts…"
                      value={form.comment}
                      onChange={(e) => setForm({ ...form, comment: e.target.value })}
                      className="rounded-none border-zinc-300"
                      data-testid="review-comment"
                    />
                    <Button type="submit" className="rounded-none bg-zinc-900 text-xs uppercase tracking-widest" data-testid="review-submit">
                      Submit Review
                    </Button>
                  </form>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>
      </div>

      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="font-display mb-8 text-2xl font-light tracking-tight">You may also like</h2>
          <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-4">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
